<template>
  <div class="patient-sms-widget" v-if="patient">
    <!-- Floating SMS Button (when closed) -->
    <PatientSmsButton
      v-if="!isOpen"
      :unread-count="unreadCount"
      :has-existing-conversation="hasExistingConversation"
      :patient-name="patient.Name || patient.PatientName"
      :is-loading="isLoading"
      :can-send-sms="canSendSMS"
      :sms-disabled-reason="smsDisabledReason"
      @click="handleSmsButtonClick"
    />
    
    <!-- SMS Panel (when open) -->
    <PatientSmsPanel
      v-if="isOpen"
      :patient="patient"
      :conversation="currentConversation"
      :messages="messages"
      :messages-loaded="messagesLoaded"
      :is-loading="isLoading"
      :has-error="hasError"
      :error-message="errorMessage"
      :outgoing-sms-number="formattedSmsNumber"
      :site-name="user && user.SiteName ? user.SiteName : 'Your practice'"
      @close="closeSms"
      @minimize="minimizeSms"
      @send-message="handleSendMessage"
      @send-wiki="handleSendWiki"
      @send-form="handleSendForm"
      @send-file="handleSendFile"
      @retry="createNewConversation"
    />
    
    <!-- Minimized State -->
    <div 
      v-if="isMinimized" 
      class="sms-minimized"
      @click="restoreSms"
    >
      <i class="fa fa-mobile"></i>
      <span class="patient-name">SMS: {{ patient.Name || patient.PatientName }}</span>
      <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount }}</span>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import PatientSmsButton from './sms/PatientSmsButton.vue'
import PatientSmsPanel from './sms/PatientSmsPanel.vue'
import mixin from '../../../mixin.js'
import { isTextablePhone, describePhoneProblem } from '@/utils/patientPhone.js'
import { getTextBlockReason, isTextBlocked } from '@/utils/textConsent.js'
import { isSmsEnabled, hasSmsNumber, SMS_DISABLED_MESSAGE } from '@/utils/siteCapability.js'

export default {
  name: 'PatientSmsWidget',
  components: {
    PatientSmsButton,
    PatientSmsPanel
  },
  mixins: [mixin],
  props: {
    patient: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      hasError: false,
      errorMessage: ''
    }
  },
  computed: {
    ...mapState('conversationsV2', [
      'patientSmsWidgetOpen',
      'patientSmsWidgetMinimized',
      'patientSmsMessages',
      'patientSmsMessagesLoaded'
    ]),
    ...mapGetters('conversationsV2', [
      'isPatientSmsMode',
      'currentPatientSmsConversation',
      'patientSmsUnreadCount'
    ]),
    
    isOpen() {
      return this.patientSmsWidgetOpen && !this.patientSmsWidgetMinimized
    },
    
    isMinimized() {
      return this.patientSmsWidgetOpen && this.patientSmsWidgetMinimized
    },
    
    hasExistingConversation() {
      return !!this.currentPatientSmsConversation
    },
    
    currentConversation() {
      return this.currentPatientSmsConversation
    },
    
    messages() {
      // Use the main conversation messages (same as main Conversations page)
      return this.$store.state.conversationsV2.messages || []
    },
    
    messagesLoaded() {
      // Use the main conversation messagesLoaded state
      return this.$store.state.conversationsV2.messagesLoaded
    },
    
    unreadCount() {
      return this.patientSmsUnreadCount
    },
    
    formattedSmsNumber() {
      if (this.allSettings && this.allSettings.GENERAL_PHONE && this.allSettings.GENERAL_PHONE.PropertyValue) {
        const raw = this.allSettings.GENERAL_PHONE.PropertyValue.trim();
        const digits = raw.replace(/\D/g, '');
        if (digits.length === 11 && digits.startsWith('1')) {
          return `(${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7, 11)}`;
        }
        if (digits.length === 10) {
          return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
        }
        return raw;
      }
      return '';
    },
    // SMS validation logic (same as PatientProfile.vue)
    canSendSMS() {
      let canSend = false;

      // isSmsEnabled: a number AND outbound SMS not switched off (WhatsApp-only office).
      if(isSmsEnabled(this.allSettings)) {
        // The ONE opt-out rule (texted STOP, OD Text-OK = No, or a server/HQ block) —
        // src/utils/textConsent.js. Replaces the old inline TxtMsgOk === 2, which missed
        // the texted-STOP ledger.
        if(isTextBlocked({ patient: this.patient, timezone: this.smsOptOutTimezone })) {
          canSend = false;
        } else {
          // Shared rule — see src/utils/patientPhone.js. GENERAL_SMS_FLAG_CHECK does not
          // bypass it; no site setting makes a malformed number deliverable.
          canSend = isTextablePhone(this.patient && this.patient.WirelessPhone);
        }
      }
      return canSend;
    },

    // The office HAS texting (else "not enabled on your account") — siteCapability.hasSmsNumber.
    smsFeatureEnabled() {
      return hasSmsNumber(this.allSettings);
    },

    smsDisabledReason() {
      // Check if SMS feature is not enabled at account level
      if (!this.smsFeatureEnabled) {
        return 'SMS feature is not enabled on your account yet';
      } else if (!isSmsEnabled(this.allSettings)) {
        return SMS_DISABLED_MESSAGE;
      } else {
        // Opt-out first (texted STOP, OD Text-OK = No, or a server/HQ block). The shared
        // rule renders the same sentence the backend would on a refusal.
        const block = getTextBlockReason({ patient: this.patient, timezone: this.smsOptOutTimezone });
        if (block.blocked) {
          return block.message;
        } else {
          // Shared copy — the operator gets the same sentence here, on the profile's
          // disabled-button tooltip, and inside the send panel.
          const phoneProblem = describePhoneProblem(this.patient && this.patient.WirelessPhone);
          if (phoneProblem) return phoneProblem;
        }
      }
      return null;
    },

    // Site timezone (GENERAL_TIMEZONE) for the shared text-consent message.
    smsOptOutTimezone() {
      return (this.allSettings && this.allSettings.GENERAL_TIMEZONE && this.allSettings.GENERAL_TIMEZONE.PropertyValue) || '';
    }
  },
  async mounted() {
    if (this.patient && this.patient.PatNum) {
      await this.initializePatientChat()
    }
  },
  watch: {
    patient: {
      handler(newPatient, oldPatient) {
        if (newPatient && newPatient.PatNum !== oldPatient?.PatNum) {
          // Reset previous patient's state before initializing new patient
          console.log('[PatientSMS] Patient changed from', oldPatient?.PatNum, 'to', newPatient?.PatNum)
          this.resetPatientSms()
          this.initializePatientChat()
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('conversationsV2', [
      'resetPatientSms',
      'initPatientSms',
      'createPatientSmsConversation',
      'sendPatientSmsMessage',
      'sendWikiLink',
      'sendFormLink',
      'fetchMessages'
    ]),
    ...mapMutations('conversationsV2', [
      'setPatientSmsWidgetOpen',
      'setPatientSmsWidgetMinimized',
      'setSelectedRoomId'
    ]),
    
    async initializePatientChat() {
      try {
        await this.initPatientSms({
          patient: this.patient,
          siteId: this.siteID
        })
      } catch (error) {
        console.error('Failed to initialize patient SMS:', error)
      }
    },
    
    async handleSmsButtonClick() {
      // Check if SMS feature is enabled at account level first
      if (!this.smsFeatureEnabled) {
        this.$toast.error('SMS feature is not enabled on your account yet. Please contact TeamIO support to enable SMS messaging for your practice.');
        return;
      }
      
      // Check if SMS is available for this specific patient
      if (!this.canSendSMS) {
        const reason = this.smsDisabledReason;
        this.$toast.error(reason || 'SMS is not available for this patient');
        return;
      }
      
      // Show loading state
      this.isLoading = true
      
      try {
        // Always open the SMS panel - backend handles conversation creation/retrieval
        if (this.hasExistingConversation) {
          // Open existing conversation
          this.openSms()
        } else {
          // Create/find conversation and open panel
          try {
            await this.createNewConversation()
            this.openSms()
          } catch (error) {
            console.error('[PatientSMS] Error creating conversation:', error)
            // Still open the panel to show the error state
            this.openSms()
          }
        }
      } finally {
        // Hide loading state
        this.isLoading = false
      }
    },
    
    async createNewConversation() {
      try {
        this.hasError = false
        this.errorMessage = ''
        this.isLoading = true
        
        const conversation = await this.createPatientSmsConversation({
          patient: this.patient,
          siteId: this.siteID
        })
        
        // Load messages for the conversation (same as main Conversations page)
        if (conversation && conversation.roomId) {
          console.log('[PatientSMS] Loading messages for conversation:', conversation.roomId)
          
          // CRITICAL: Set selected room ID BEFORE fetchMessages (like main Conversations page)
          this.setSelectedRoomId(conversation.roomId)
          console.log('[PatientSMS] Set selected room ID for real-time listener:', conversation.roomId)
          
          await this.fetchMessages({ 
            roomId: conversation.roomId, 
            messagesPerPage: 20 
          })
          console.log('[PatientSMS] Messages loaded successfully with real-time listener')
        }
      } catch (error) {
        console.error('Failed to create patient SMS conversation:', error)
        this.hasError = true
        this.errorMessage = 'Unable to load SMS conversation. Please try again or contact support if the issue persists.'
      } finally {
        this.isLoading = false
      }
    },
    
    openSms() {
      this.setPatientSmsWidgetOpen(true)
    },
    
    closeSms() {
      this.setPatientSmsWidgetOpen(false)
      // Clean up real-time listener when closing
      this.setSelectedRoomId(null)
    },
    
    minimizeSms() {
      this.setPatientSmsWidgetMinimized(true)
    },
    
    restoreSms() {
      this.setPatientSmsWidgetMinimized(false)
    },
    
    async handleSendMessage(messageData) {
      try {
        // If no conversation exists, create one first
        if (!this.currentConversation) {
          console.log('[PatientSMS] No conversation exists, creating one before sending message')
          await this.createNewConversation()
        }
        
        // Now send the message
        if (this.currentConversation && this.currentConversation.roomId) {
          await this.sendPatientSmsMessage({
            content: messageData.content,
            conversationSID: this.currentConversation.roomId,
            file: messageData.file
          })
        } else {
          throw new Error('Failed to create conversation before sending message')
        }
      } catch (error) {
        console.error('Failed to send SMS message:', error)
        alert('Failed to send SMS message. Please try again.')
      }
    },
    
    async handleSendWiki(wikiData) {
      try {
        // If no conversation exists, create one first
        if (!this.currentConversation) {
          console.log('[PatientSMS] No conversation exists, creating one before sending wiki')
          await this.createNewConversation()
        }
        
        if (this.currentConversation && this.currentConversation.roomId) {
          await this.sendWikiLink({
            wikiId: wikiData.WikiPageID,
            wikiName: wikiData.Name,
            wikiContent: wikiData.Content,
            editedContent: wikiData.EditedContent || null,
            patientId: this.patient.PatNum,
            conversationId: this.currentConversation.roomId
          })
        } else {
          throw new Error('Failed to create conversation before sending wiki')
        }
      } catch (error) {
        console.error('Failed to send wiki link:', error)
        // A tagged /send-text-msg refusal (opt-out / undeliverable) carries the human reason;
        // show it rather than the generic retry copy — retrying a refusal never helps.
        this.$toast.error(error && error.isSendRefusal ? error.message : 'Failed to send wiki link. Please try again.')
      }
    },
    
    async handleSendForm(formData) {
      try {
        // If no conversation exists, create one first
        if (!this.currentConversation) {
          console.log('[PatientSMS] No conversation exists, creating one before sending form')
          await this.createNewConversation()
        }
        
        if (this.currentConversation && this.currentConversation.roomId) {
          await this.sendFormLink({
            formId: formData.FormIDForWeb,
            formName: formData.Name || formData.FormName,
            patientId: this.patient.PatNum,
            conversationId: this.currentConversation.roomId
          })
        } else {
          throw new Error('Failed to create conversation before sending form')
        }
      } catch (error) {
        console.error('Failed to send form link:', error)
        // A tagged /send-text-msg refusal (opt-out / undeliverable) carries the human reason.
        this.$toast.error(error && error.isSendRefusal ? error.message : 'Failed to send form link. Please try again.')
      }
    },
    
    async handleSendFile(fileData) {
      try {
        // If no conversation exists, create one first
        if (!this.currentConversation) {
          console.log('[PatientSMS] No conversation exists, creating one before sending file')
          await this.createNewConversation()
        }
        
        // Now send the file using the same logic as main Conversations page
        if (this.currentConversation && this.currentConversation.roomId) {
          await this.sendPatientSmsMessage({
            content: fileData.name || 'File attachment',
            conversationSID: this.currentConversation.roomId,
            file: fileData
          })
        } else {
          throw new Error('Failed to create conversation before sending file')
        }
      } catch (error) {
        console.error('Failed to send file via SMS:', error)
        this.$toast.error('Failed to send file via SMS. Please try again.')
      }
    }
  },
  
  beforeDestroy() {
    // Clean up patient SMS state when component is destroyed
    if (this.isPatientSmsMode) {
      this.resetPatientSms()
    }
  }
}
</script>

<style lang="scss" scoped>
.patient-sms-widget {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  font-family: 'Quicksand', sans-serif;
}

.sms-minimized {
  display: flex;
  align-items: center;
  background: #1976d2;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: all 0.3s ease;
  
  &:hover {
    background: #1565c0;
    transform: translateY(-2px);
  }
  
  .fa {
    margin-right: 8px;
  }
  
  .patient-name {
    font-weight: 600;
    margin-right: 8px;
  }
  
  .unread-badge {
    background: #f44336;
    color: white;
    border-radius: 10px;
    padding: 2px 6px;
    font-size: 12px;
    font-weight: bold;
    min-width: 18px;
    text-align: center;
  }
}

// Mobile responsiveness
@media (max-width: 768px) {
  .patient-sms-widget {
    bottom: 15px;
    right: 15px;
  }
}
</style>
